<template>
    <div class="doc-analise-page">
        <div v-if="isLoading" class="loading-overlay"><p>Carregando...</p></div>

        <div v-if="msgAlerta" class="info-alert row-space clickable" @click="msgAlerta = null">
            <span class="text">{{ msgAlerta }}</span>
        </div>

        <div class="page-header row-space">
            <div>
                <div class="page-title">Análise de documentos pendentes</div>
                <div class="page-subtitle">
                    {{ lsDados.length }} documento(s) aguardando análise
                </div>
            </div>

            <button class="btn-small" @click="closePage">Sair</button>
        </div>

        <div v-if="gruposEstabelecimento.length === 0" class="empty-state">
            Nenhum documento pendente encontrado.
        </div>

        <div
            v-for="grupo in gruposEstabelecimento"
            :key="grupo.idestabelecimento"
            class="estab-block row-space"
        >
            <div class="estab-header" @click="toggleEstabelecimento(grupo.idestabelecimento)">
                <div>
                    <div class="estab-title">{{ grupo.nmestabelecimento }}</div>
                    <div class="estab-subtitle">{{ grupo.nmsim }}</div>
                </div>

                <div class="estab-summary">
                    <span>{{ grupo.itens.length }} pendente(s)</span>
                    <strong>{{ isAberto(grupo.idestabelecimento) ? '−' : '+' }}</strong>
                </div>
            </div>

            <div v-show="isAberto(grupo.idestabelecimento)" class="doc-list">
                <div
                    v-for="doc in grupo.itens"
                    :key="doc.id"
                    class="doc-card"
                    :class="classeVencimento(doc)"
                >
                    <div class="doc-card-main">
                        <div class="doc-name">{{ doc.nmdoc || 'Documento sem identificação' }}</div>
                        <div class="doc-type">{{ doc.nmtpdoc || 'Tipo de documento não vinculado' }}</div>

                        <div class="doc-meta">
                            <span>Anexado em: {{ formatDataBR(doc.dtanexo) }}</span>
                            <span>Validade atual: {{ formatDataBR(doc.dtvalidade) }}</span>
                            <span>{{ doc.tiponm }}</span>
                            <span>{{ doc.situacaonm }}</span>
                        </div>

                        <div class="doc-actions">
                            <button
                                type="button"
                                class="btn-doc-open"
                                @click="openAnexo(doc.id)"
                            >
                                 {{ idAnexoAbrindo === doc.id ? 'Abrindo...' : 'Abrir documento' }}
                            </button>
                        </div>
                    </div>

                    <div class="doc-status">
                        <span class="status-badge">{{ doc.stvencimento }}</span>
                        <small>{{ doc.dias_prazo }} dia(s)</small>
                    </div>

                    <div class="doc-analysis">
                        <div v-if="doc.ckvalidade=='S'" class="row row-space">
                            <div class="form-item-data">
                                <label class="label-caption">Validade confirmada</label>
                                <input type="date" v-model="getAnalise(doc.id).dtValidade" />
                            </div>
                        </div>

                        <div class="row-start">
                            <button
                                type="button"
                                class="btn-confirm btn-action"
                                @click="salvarAnalise(doc.id, 'S', doc.ckvalidade)"
                            >
                                Aprovar
                            </button>

                            <button
                                type="button"
                                class="btn-cancel btn-action"
                                @click="confirmarNaoAprovacao(doc.id)"
                            >
                                Não aprovar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    
    <ModalJustificativaTexto ref="modalJustificativa" />
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { Funcoes } from '@/funcoes/funcoes';
import { globalStore } from '@/globals';
import ModalJustificativaTexto from '@/components/modals/ModalJustificativaTexto.vue';

const emit = defineEmits(['close']);

const modalJustificativa = ref(null);

const idAnexoAbrindo = ref(null);
const isLoading = ref(false);
const lsDados = ref([]);
const msgAlerta = ref(null);

const estabelecimentosAbertos = ref({});
const analises = ref({});

const closePage = () => emit('close');

const gruposEstabelecimento = computed(() => {
    const map = {};

    lsDados.value.forEach(obj => {
        const id = obj.idestabelecimento || 0;

        if (!map[id]) {
            map[id] = {
                idestabelecimento: id,
                nmestabelecimento: obj.nmestabelecimento || 'Sem estabelecimento',
                nmsim: obj.nmsim,
                itens: []
            };
        }

        map[id].itens.push(obj);
    });

    return Object.values(map);
});


async function confirmarNaoAprovacao(idAnexo) {
    const rModal = await modalJustificativa.value.show();
    if (!rModal.confirmado) return;

    const analise = getAnalise(idAnexo);
    analise.obsAnalise = rModal.justificativa;

    await salvarAnalise(idAnexo, 'N', 'N');
}


async function openAnexo(idAnexo) {
    try {
        msgAlerta.value = null;
        idAnexoAbrindo.value = idAnexo;
        const r = await Api.genericPost('/operacoes/solicitar/download', {id: idAnexo});
        if (r.bolok === 1){
            window.open(r.download_url, '_blank');
        }
    } catch (error){msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {idAnexoAbrindo.value = null;}
}


function toggleEstabelecimento(id) {
    estabelecimentosAbertos.value[id] = !estabelecimentosAbertos.value[id];
}

function isAberto(id) {
    return estabelecimentosAbertos.value[id] === true;
}

function getAnalise(idAnexo) {
    if (!analises.value[idAnexo]) {
        analises.value[idAnexo] = {
            dtValidade: null,
            obsAnalise: ''
        };
    }

    return analises.value[idAnexo];
}

function formatDataBR(value) {
    if (!value) return '-';

    const [ano, mes, dia] = value.substring(0, 10).split('-');
    if (!ano || !mes || !dia) return value;

    return `${dia}/${mes}/${ano}`;
}

function classeVencimento(obj) {
    if (obj.stvencimento === 'VENCIDO') return 'vencido';
    return 'prazo';
}

async function lerDados() {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const dados = {
            idep: globalStore.idep,
            idsim: globalStore.idsim,
            intparam3: 1,
            strparam1: 'NMESTABELECIMENTO, IDESTABELECIMENTO'
        };

        const req = await Api.genericPost('/listas/anexos', dados);
        lsDados.value = req || [];

        gruposEstabelecimento.value.forEach(grupo => {
            estabelecimentosAbertos.value[grupo.idestabelecimento] = true;
        });
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function salvarAnalise(idAnexo, aprovadoSN, ckValidade) {
    const analise = getAnalise(idAnexo);
    console.log('ANALISE SALVAR:', analise);

    msgAlerta.value = null;

    if (aprovadoSN === 'S' && ckValidade === 'S' && !analise.dtValidade) {
        msgAlerta.value = 'Necessário confirmação da data de validade!';
        return;
    }

    if (aprovadoSN === 'N' && !analise.obsAnalise?.trim()) {
        msgAlerta.value = 'Necessário informar justificativa';
        return;
    }

    try {
        isLoading.value = true;

        const dados = {
            id: idAnexo,
            dtvalidade: (ckValidade === 'S') ? analise.dtValidade : null,
            dhanalise: Funcoes.strDataHoraAtual(),
            idloginanalise: globalStore.idlogin,
            iduanalise: globalStore.id,
            obsanalise: analise.obsAnalise,
            homologado: aprovadoSN
        };

        const ret = await Api.genericPut('/cad/anexo', dados);

        if (ret.bolok == 0) {
            msgAlerta.value = `ATENÇÃO: ${ret.erro}`;
            return;
        }

        delete analises.value[idAnexo];
        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

onMounted(lerDados);
</script>


<style scoped>
.doc-analise-page {
    width: 100%;
    text-align: left;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;

    background: #fff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.page-title {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
}

.page-subtitle {
    font-size: 13px;
    color: #64748b;
    margin-top: 3px;
}

.estab-block {
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.estab-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    padding: 12px 14px;
    background: #f8fafc;
    cursor: pointer;
    border-bottom: 1px solid #e5e7eb;
}

.estab-header:hover {
    background: #f1f5f9;
}

.estab-title {
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
}

.estab-subtitle {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
}

.estab-summary {
    display: flex;
    align-items: center;
    gap: 12px;
}

.estab-summary span {
    font-size: 12px;
    color: #475569;
    background: #e2e8f0;
    border-radius: 999px;
    padding: 4px 9px;
}

.estab-summary strong {
    font-size: 24px;
    color: #334155;
    line-height: 1;
}

.doc-list {
    padding: 12px;
    background: #fff;
}

.doc-card {
    display: grid;
    grid-template-columns: 1fr 110px 360px;
    gap: 14px;
    align-items: start;

    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 10px;
    background: #fff;
}

.doc-card.vencido {
    border-left: 5px solid #dc2626;
}

.doc-card.prazo {
    border-left: 5px solid #16a34a;
}

.doc-name {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
}

.doc-type {
    font-size: 12px;
    color: #64748b;
    margin-top: 3px;
}

.doc-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    font-size: 11px;
    color: #64748b;
}

.doc-meta span {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 3px 7px;
}

.doc-status {
    text-align: center;
}

.status-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    border-radius: 999px;
    padding: 5px 8px;
    background: #f1f5f9;
    color: #334155;
}

.doc-status small {
    display: block;
    margin-top: 5px;
    color: #64748b;
}

.doc-analysis {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 10px;
}

.btn-action {
    width: 120px;
    font-size: 12px;
    padding: 5px 0;
}

.empty-state {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 10px;
    padding: 22px;
    color: #64748b;
    text-align: center;
    font-size: 13px;
}

@media (max-width: 1100px) {
    .doc-card {
        grid-template-columns: 1fr;
    }

    .doc-status {
        text-align: left;
    }

    .doc-analysis {
        width: 100%;
    }
}


.btn-doc-open {
    margin-top: 10px;
    width: auto;
    min-width: 120px;
    padding: 5px 10px;
    font-size: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #f8fafc;
    color: #334155;
    cursor: pointer;
}

.btn-doc-open:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.doc-analysis .btn-action {
    width: 120px;
}

.doc-card,
.doc-card-main,
.doc-name,
.doc-type,
.doc-meta,
.doc-actions,
.doc-analysis {
    text-align: left;
}

.doc-status,
.doc-status * {
    text-align: center;
}

.doc-analysis .btn-action {
    width: 120px;
}
</style>